import { useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import AmountInput from 'components/common/AmountInput';
import { CYRILLIC_REGEXP } from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import i18nContext from 'components/i18n-context';
import ValidatePaymentDetailsModel from 'components/Templates/models/ValidatePaymentDetailsModel';
import { CRYPTO_CURRENCY_PRECISION } from 'services/currenciesConstant';
import Input from 'uikit/Input/Input';
import Button from 'uikit/Button/Button';
import './PaymentDetailsStep.scss';
import { getTemplateStepErrorMessage } from 'components/Templates/utils/getTemplateStepErrorMessage';

const PaymentDetailsStep = ({ paymentStore, customerNumber, userAccounts }) => {
  const i18n = useContext(i18nContext);
  const isCryptoPayment = !!paymentStore.templateData?.isCryptoPayment;

  // Find account with matching currency to get currency_precision
  const matchingAccount = (userAccounts || []).find(
    (account) => account?.currency === paymentStore.templateData?.currency
  );
  const currencyPrecision = matchingAccount?.currency_precision;

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      amount: paymentStore.templateData?.amount || '',
      transferDetails: paymentStore.templateData?.transferDetails || ''
    },
    validationSchema: Yup.object().shape({
      amount: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      transferDetails: Yup.string().when([], {
        is: () => !isCryptoPayment,
        then: (schema) =>
          schema
            .trim()
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(255, i18n.getMessage('client.validation.message.maxSize', { size: 255 }))
            .test(
              'no-cyrillic',
              i18n.getMessage('client.validation.message.noCyrillic'),
              (value) => !CYRILLIC_REGEXP.test(value || '')
            ),
        otherwise: (schema) => schema
      })
    }),
    onSubmit: async (values) => {
      const requestData = ValidatePaymentDetailsModel({ ...paymentStore.templateData, ...values });

      paymentStore.setTemplateData({ ...paymentStore.templateData, ...values });

      await paymentStore.validateTemplatePaymentDetails(customerNumber, requestData);
    }
  });

  const { values, errors, handleSubmit, handleChange } = form;

  return (
    <form onSubmit={form.handleSubmit} className={'payment-details-step'}>
      <div className={'payment-details-step-inputs-wrapper'}>
        <AmountInput
          className={'payment-details-step-input'}
          name={'amount'}
          label={i18n.getMessage('templates.form.amount.label')}
          placeholder={i18n.getMessage('templates.form.amount.placeholder')}
          value={`${values.amount}`}
          error={errors.amount || getTemplateStepErrorMessage(i18n, paymentStore.error, 'amount')}
          onChange={handleChange}
          precision={isCryptoPayment ? CRYPTO_CURRENCY_PRECISION : (currencyPrecision ?? 2)}
          currency={paymentStore.templateData?.currency}
          isRequired
        />
        {!isCryptoPayment && (
          <Input
            className={'payment-details-step-input'}
            name={'transferDetails'}
            label={i18n.getMessage('templates.form.transferDetails.label')}
            placeholder={i18n.getMessage('templates.form.transferDetails.placeholder')}
            value={values.transferDetails}
            error={errors.transferDetails || getTemplateStepErrorMessage(i18n, paymentStore.error, 'transferDetails')}
            onChange={handleChange}
            isRequired
          />
        )}
      </div>
      <Button
        className={'payment-details-step-next-button'}
        type={'primary'}
        roleType={'submit'}
        onClick={handleSubmit}
        isDisabled={paymentStore.isLoading}
      >
        {i18n.getMessage('templates.form.button.next')}
      </Button>
    </form>
  );
};

PaymentDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  paymentStore: MobXPropTypes.observableObject,
  userAccounts: MobXPropTypes.observableArray
};

export default inject((stores) => ({
  paymentStore: stores.paymentStore,
  userAccounts: stores.userStore?.userAccounts
}))(observer(PaymentDetailsStep));
